import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Minus, Search, AlertTriangle, Save, X, Camera, ImageIcon, ArrowRightLeft, ShoppingCart, Check, XCircle, PackagePlus } from "lucide-react";

// Auth-aware image URL builder — appends ?auth=<jwt> so <img src> works without headers.
const buildImgUrl = (storagePath) => {
  const token = localStorage.getItem("token");
  const base = axios.defaults.baseURL || "";
  return `${base}/files/${storagePath}?auth=${encodeURIComponent(token || "")}`;
};

/**
 * Truck Inventory
 * - Tech: manages their own truck. Can also search across all trucks.
 * - Admin/Part Manager: view/edit any tech's inventory + search across all trucks.
 *
 * Low-stock alerts (Telegram + auto-draft replacement order) are handled server-side
 * whenever a row's quantity crosses its min_threshold.
 */
export default function TruckInventory({ user }) {
  const isPrivileged = user?.role === "admin" || user?.role === "part_manager";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTechId, setSelectedTechId] = useState(""); // admin: filter by specific tech
  const [techs, setTechs] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    part_name: "",
    part_number: "",
    quantity: 1,
    min_threshold: 1,
    notes: "",
    tech_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  // Photo state
  const [pendingPhotos, setPendingPhotos] = useState([]); // File[] queued during Add flow
  const rowUploadRef = useRef(null);
  const rowUploadTargetRef = useRef(null); // item.id currently receiving a photo (via ref to avoid stale-state race)
  const [uploadingRowId, setUploadingRowId] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, item: null, index: 0 });

  // Transfer + Quick-order dialogs
  const [transferItem, setTransferItem] = useState(null);
  const [transferQty, setTransferQty] = useState(1);
  const [transferTechId, setTransferTechId] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [quickOrderItem, setQuickOrderItem] = useState(null);
  const [quickOrderForm, setQuickOrderForm] = useState({ job_number: "", quantity: 1, part_cost: "" });
  const [quickOrderPreview, setQuickOrderPreview] = useState(null);
  const [quickOrderBusy, setQuickOrderBusy] = useState(false);

  // Incoming transfer requests
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [outgoingRequests, setOutgoingRequests] = useState([]);
  const [requestsBusy, setRequestsBusy] = useState({});

  // Truck Restock Orders (peer to Admin)
  const [brands, setBrands] = useState([]);
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockForm, setRestockForm] = useState({ part_name: "", part_number: "", brand: "", quantity: 1, notes: "", source_item_id: null });
  const [restockCustomBrand, setRestockCustomBrand] = useState("");
  const [restockBusy, setRestockBusy] = useState(false);
  const [truckOrders, setTruckOrders] = useState([]);
  const [approveOrder, setApproveOrder] = useState(null); // TruckOrderRequest being approved (admin)
  const [approveForm, setApproveForm] = useState({ part_source: "Marcone", part_cost: "" });
  const [approvePreview, setApprovePreview] = useState(null);
  const [approveBusy, setApproveBusy] = useState(false);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [declineReason, setDeclineReason] = useState("");

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Load techs list (admin/part_manager sees full list; regular techs need the list for the
  // Transfer-to-Tech dropdown too)
  useEffect(() => {
    axios.get("/auth/techs").then((r) => setTechs(r.data || [])).catch(() => {});
  }, []);

  const loadRequests = async () => {
    try {
      const res = await axios.get("/truck-transfers", { params: { status: "pending" } });
      const all = res.data || [];
      setIncomingRequests(all.filter((r) => r.to_tech_id === user?.id));
      setOutgoingRequests(all.filter((r) => r.from_tech_id === user?.id));
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadRequests();
    loadTruckOrders();
    const t = setInterval(() => { loadRequests(); loadTruckOrders(); }, 30000);
    return () => clearInterval(t);
  }, [user?.id]);

  const loadTruckOrders = async () => {
    try {
      const params = isPrivileged ? {} : { };
      const res = await axios.get("/truck-orders", { params });
      setTruckOrders(res.data || []);
    } catch {}
  };

  useEffect(() => {
    axios.get("/brands").then((r) => setBrands(r.data?.brands || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      else if (isPrivileged && selectedTechId) params.tech_id = selectedTechId;
      const res = await axios.get("/truck-inventory", { params });
      setItems(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load truck inventory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [debouncedSearch, selectedTechId]);

  const isSearching = Boolean(debouncedSearch);
  const showTechColumn = isSearching || isPrivileged;

  const uploadPhotoForItem = async (itemId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await axios.post(`/truck-inventory/${itemId}/images`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.path;
  };

  const submitAdd = async () => {
    if (!form.part_name.trim() || !form.part_number.trim()) {
      toast.error("Part name and part number are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        part_name: form.part_name.trim(),
        part_number: form.part_number.trim(),
        quantity: Math.max(parseInt(form.quantity, 10) || 0, 0),
        min_threshold: Math.max(parseInt(form.min_threshold, 10) || 0, 0),
        notes: form.notes || null,
      };
      if (isPrivileged && form.tech_id) payload.tech_id = form.tech_id;
      const res = await axios.post("/truck-inventory", payload);
      const newId = res.data?.id;

      // Upload any queued photos in parallel
      if (newId && pendingPhotos.length > 0) {
        toast.info(`Uploading ${pendingPhotos.length} photo${pendingPhotos.length > 1 ? "s" : ""}...`);
        const results = await Promise.allSettled(
          pendingPhotos.map((f) => uploadPhotoForItem(newId, f))
        );
        const failed = results.filter((r) => r.status === "rejected").length;
        if (failed > 0) toast.error(`${failed} photo${failed > 1 ? "s" : ""} failed to upload`);
      }

      toast.success("Added to truck inventory");
      setAddOpen(false);
      setForm({ part_name: "", part_number: "", quantity: 1, min_threshold: 1, notes: "", tech_id: "" });
      setPendingPhotos([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add item");
    } finally {
      setSaving(false);
    }
  };

  const onPickPhotos = (files) => {
    if (!files || files.length === 0) return;
    // IMPORTANT: capture into a static array immediately. The inline
    // `e.target.value = ""` reset (below in onChange) clears the FileList
    // reference before React can process the lazy setState update.
    const arr = Array.from(files);
    setPendingPhotos((prev) => [...prev, ...arr]);
  };

  const removePendingPhoto = (idx) => {
    setPendingPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const addPhotoToRow = async (item, file) => {
    if (!file) return;
    setUploadingRowId(item.id);
    try {
      await uploadPhotoForItem(item.id, file);
      toast.success("Photo added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingRowId(null);
      rowUploadTargetRef.current = null;
    }
  };

  const deleteRowImage = async (item, path) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await axios.delete(`/truck-inventory/${item.id}/images`, { params: { path } });
      toast.success("Photo removed");
      // update lightbox / local state
      setItems((prev) => prev.map((it) =>
        it.id === item.id ? { ...it, images: (it.images || []).filter((p) => p !== path) } : it
      ));
      setLightbox((lb) => lb.open && lb.item?.id === item.id
        ? { ...lb, item: { ...lb.item, images: (lb.item.images || []).filter((p) => p !== path) } }
        : lb);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove photo");
    }
  };

  const adjustQty = async (item, delta) => {
    const newQty = Math.max((item.quantity || 0) + delta, 0);
    try {
      await axios.patch(`/truck-inventory/${item.id}`, { quantity: newQty });
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, quantity: newQty } : it)));
      if (newQty <= (item.min_threshold || 1) && item.quantity > (item.min_threshold || 1)) {
        toast.warning(`Low stock alert sent for ${item.part_number}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update quantity");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({
      part_name: item.part_name,
      part_number: item.part_number,
      quantity: item.quantity,
      min_threshold: item.min_threshold,
      notes: item.notes || "",
    });
  };

  const saveEdit = async () => {
    try {
      const payload = {
        part_name: editDraft.part_name.trim(),
        part_number: editDraft.part_number.trim(),
        quantity: Math.max(parseInt(editDraft.quantity, 10) || 0, 0),
        min_threshold: Math.max(parseInt(editDraft.min_threshold, 10) || 0, 0),
        notes: editDraft.notes || null,
      };
      await axios.patch(`/truck-inventory/${editingId}`, payload);
      toast.success("Saved");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Delete "${item.part_name}" (${item.part_number}) from ${item.tech_name}'s truck?`)) return;
    try {
      await axios.delete(`/truck-inventory/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const canEditRow = (item) => isPrivileged || item.tech_id === user?.id;

  // ===== Transfer + Quick Order + Requests =====
  const openTransfer = (item) => {
    setTransferItem(item);
    setTransferQty(1);
    setTransferTechId("");
  };

  const submitTransfer = async () => {
    if (!transferItem) return;
    if (!transferTechId) {
      toast.error("Pick a tech");
      return;
    }
    const qty = parseInt(transferQty, 10) || 0;
    if (qty <= 0 || qty > (transferItem.quantity || 0)) {
      toast.error(`Quantity must be between 1 and ${transferItem.quantity}`);
      return;
    }
    setTransferBusy(true);
    try {
      await axios.post(`/truck-inventory/${transferItem.id}/transfer`, {
        to_tech_id: transferTechId,
        quantity: qty,
      });
      toast.success("Transfer request sent");
      setTransferItem(null);
      load();
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    } finally {
      setTransferBusy(false);
    }
  };

  const openQuickOrder = (item) => {
    setQuickOrderItem(item);
    setQuickOrderForm({ job_number: "", quantity: 1, part_cost: "" });
    setQuickOrderPreview(null);
  };

  const calcQuickOrderPreview = async () => {
    const cost = parseFloat(quickOrderForm.part_cost);
    if (!cost || cost <= 0) {
      toast.error("Enter a valid part cost");
      return;
    }
    try {
      const res = await axios.post("/calculator/markup", {
        part_source: "Truck Part",
        part_cost: cost,
        quantity: parseInt(quickOrderForm.quantity, 10) || 1,
      });
      setQuickOrderPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const submitQuickOrder = async () => {
    if (!quickOrderItem) return;
    const cost = parseFloat(quickOrderForm.part_cost);
    const qty = parseInt(quickOrderForm.quantity, 10) || 1;
    if (!quickOrderForm.job_number.trim()) {
      toast.error("Enter a job number");
      return;
    }
    if (!cost || cost <= 0) {
      toast.error("Enter a valid part cost");
      return;
    }
    if (qty > (quickOrderItem.quantity || 0)) {
      toast.error(`Only ${quickOrderItem.quantity} available in your truck`);
      return;
    }
    setQuickOrderBusy(true);
    try {
      // Ensure preview is computed
      let calc = quickOrderPreview;
      if (!calc) {
        const r = await axios.post("/calculator/markup", {
          part_source: "Truck Part",
          part_cost: cost,
          quantity: qty,
        });
        calc = r.data;
      }
      await axios.post("/orders", {
        job_number: quickOrderForm.job_number.trim(),
        part_number: quickOrderItem.part_number,
        part_source: "Truck Part",
        quantity: qty,
        part_cost: cost,
        calculated_price: calc.total_price ?? calc.total_per_unit ?? 0,
        needs_replacement: true,
        assigned_tech_name: quickOrderItem.tech_name,
      });
      toast.success("Order created — inventory decremented");
      setQuickOrderItem(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order failed");
    } finally {
      setQuickOrderBusy(false);
    }
  };

  const respondRequest = async (req, action) => {
    setRequestsBusy((prev) => ({ ...prev, [req.id]: true }));
    try {
      await axios.post(`/truck-transfers/${req.id}/${action}`);
      toast.success(action === "accept" ? "Transfer accepted" : "Transfer declined");
      loadRequests();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setRequestsBusy((prev) => {
        const { [req.id]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // ===== Truck Restock Orders =====
  const openRestockDialog = (item = null) => {
    if (item) {
      setRestockForm({
        part_name: item.part_name, part_number: item.part_number,
        brand: "", quantity: item.min_threshold || 1, notes: "",
        source_item_id: item.id,
      });
    } else {
      setRestockForm({ part_name: "", part_number: "", brand: "", quantity: 1, notes: "", source_item_id: null });
    }
    setRestockCustomBrand("");
    setRestockOpen(true);
  };

  const submitRestock = async () => {
    const effectiveBrand = restockForm.brand === "Other" ? restockCustomBrand.trim() : (restockForm.brand || "").trim();
    if (!restockForm.part_name.trim() || !restockForm.part_number.trim()) {
      toast.error("Part name and part number are required"); return;
    }
    if (!effectiveBrand) { toast.error("Pick a brand"); return; }
    const qty = parseInt(restockForm.quantity, 10) || 0;
    if (qty <= 0) { toast.error("Quantity must be positive"); return; }
    setRestockBusy(true);
    try {
      await axios.post("/truck-orders", {
        part_name: restockForm.part_name.trim(),
        part_number: restockForm.part_number.trim(),
        brand: effectiveBrand,
        quantity: qty,
        notes: restockForm.notes || null,
        source_item_id: restockForm.source_item_id,
      });
      toast.success("Restock request sent to admin");
      setRestockOpen(false);
      loadTruckOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit restock");
    } finally {
      setRestockBusy(false);
    }
  };

  const openApprove = (tor) => {
    setApproveOrder(tor);
    setApproveForm({ part_source: "Marcone", part_cost: "" });
    setApprovePreview(null);
  };

  const calcApprovePreview = async () => {
    const cost = parseFloat(approveForm.part_cost);
    if (!cost || cost <= 0) { toast.error("Enter a valid part cost"); return; }
    try {
      const r = await axios.post("/calculator/markup", {
        part_source: approveForm.part_source, part_cost: cost,
        quantity: parseInt(approveOrder.quantity, 10) || 1,
      });
      setApprovePreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
  };

  const submitApprove = async () => {
    if (!approveOrder) return;
    const cost = parseFloat(approveForm.part_cost);
    if (!cost || cost <= 0) { toast.error("Enter a valid part cost"); return; }
    setApproveBusy(true);
    try {
      let calc = approvePreview;
      if (!calc) {
        const r = await axios.post("/calculator/markup", {
          part_source: approveForm.part_source, part_cost: cost,
          quantity: approveOrder.quantity,
        });
        calc = r.data;
      }
      await axios.post("/orders", {
        job_number: `RESTOCK-${approveOrder.part_number}`,
        part_number: approveOrder.part_number,
        part_source: approveForm.part_source,
        brand: approveOrder.brand,
        quantity: approveOrder.quantity,
        part_cost: cost,
        calculated_price: calc.total_price ?? 0,
        needs_replacement: false,
        assigned_tech_name: approveOrder.requester_tech_name,
        linked_truck_order_id: approveOrder.id,
      });
      toast.success("Approved — order created; tech's truck will update when marked Ordered");
      setApproveOrder(null);
      loadTruckOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed");
    } finally {
      setApproveBusy(false);
    }
  };

  const submitDecline = async () => {
    if (!declineTarget) return;
    try {
      await axios.post(`/truck-orders/${declineTarget.id}/decline`, { reason: declineReason || null });
      toast.success("Declined");
      setDeclineTarget(null);
      setDeclineReason("");
      loadTruckOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decline failed");
    }
  };


  const stats = useMemo(() => {
    const lowRows = items.filter((it) => (it.quantity || 0) <= (it.min_threshold || 1));
    return { total: items.length, low: lowRows.length };
  }, [items]);

  return (
    <div className="space-y-4">
      {/* Incoming transfer requests */}
      {incomingRequests.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
              <ArrowRightLeft className="w-4 h-4" />
              Incoming Transfer Requests ({incomingRequests.length})
            </div>
            {incomingRequests.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 bg-white rounded-md p-2 border border-amber-200">
                <div className="text-sm min-w-0 flex-1">
                  <p className="font-medium truncate">
                    {r.from_tech_name} → you
                  </p>
                  <p className="text-xs text-slate-600 truncate">
                    {r.quantity}× <span className="font-mono">{r.part_number}</span> — {r.part_name}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 h-8"
                    disabled={requestsBusy[r.id]}
                    onClick={() => respondRequest(r, "accept")}
                    data-testid={`transfer-accept-${r.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-red-600 border-red-300"
                    disabled={requestsBusy[r.id]}
                    onClick={() => respondRequest(r, "decline")}
                    data-testid={`transfer-decline-${r.id}`}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Outgoing requests (compact) */}
      {outgoingRequests.length > 0 && (
        <div className="text-xs text-slate-500 px-1">
          {outgoingRequests.length} pending transfer{outgoingRequests.length > 1 ? "s" : ""} awaiting response
        </div>
      )}

      {/* Header + Add button */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold">Truck Inventory</h2>
          <p className="text-xs text-slate-500">
            {isSearching
              ? "Searching across all trucks"
              : isPrivileged
              ? "Viewing all techs (filter below)"
              : "Your truck"}
            {" · "}
            {stats.total} item{stats.total !== 1 ? "s" : ""}
            {stats.low > 0 && (
              <span className="text-amber-600 font-medium"> · {stats.low} low</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => openRestockDialog()}
            variant="outline"
            className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
            data-testid="restock-request-btn"
            title="Request Restock from Admin"
          >
            <PackagePlus className="w-4 h-4 mr-1" /> Request Restock
          </Button>
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="truck-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Part
          </Button>
        </div>
      </div>

      {/* Admin: Incoming Restock Requests */}
      {isPrivileged && truckOrders.filter((t) => t.status === "pending").length > 0 && (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
              <PackagePlus className="w-4 h-4" />
              Incoming Restock Requests ({truckOrders.filter((t) => t.status === "pending").length})
            </div>
            {truckOrders.filter((t) => t.status === "pending").map((tor) => (
              <div key={tor.id} className="flex items-center justify-between gap-2 bg-white rounded-md p-2 border border-emerald-200">
                <div className="text-sm min-w-0 flex-1">
                  <p className="font-medium truncate">{tor.requester_tech_name}</p>
                  <p className="text-xs text-slate-600 truncate">
                    {tor.quantity}× <span className="font-mono">{tor.part_number}</span> — {tor.part_name}
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">🏷️ {tor.brand}</span>
                  </p>
                  {tor.notes && <p className="text-xs text-slate-500 truncate">📝 {tor.notes}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 h-8"
                    onClick={() => openApprove(tor)}
                    data-testid={`restock-approve-${tor.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-red-600 border-red-300"
                    onClick={() => setDeclineTarget(tor)}
                    data-testid={`restock-decline-${tor.id}`}
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tech: my recent restock request statuses (last 5) */}
      {!isPrivileged && truckOrders.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">My Restock Requests</p>
            <div className="space-y-1">
              {truckOrders.slice(0, 5).map((tor) => (
                <div key={tor.id} className="flex items-center justify-between text-xs">
                  <span className="truncate">
                    {tor.quantity}× <span className="font-mono">{tor.part_number}</span>
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                    tor.status === "pending" ? "bg-amber-100 text-amber-700"
                    : tor.status === "approved" ? "bg-emerald-100 text-emerald-700"
                    : "bg-red-100 text-red-700"
                  }`}>
                    {tor.status}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search + tech filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search all trucks by part #, name, or tech"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="truck-search-input"
          />
        </div>
        {isPrivileged && !isSearching && (
          <select
            className="border rounded-md px-3 py-2 text-sm bg-white"
            value={selectedTechId}
            onChange={(e) => setSelectedTechId(e.target.value)}
            data-testid="truck-tech-filter"
          >
            <option value="">All techs</option>
            {techs.map((t) => (
              <option key={t.id} value={t.id}>{t.full_name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Add form (inline) */}
      {addOpen && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              Add Part to Truck
              <Button variant="ghost" size="sm" onClick={() => setAddOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Part Name</Label>
              <Input
                value={form.part_name}
                onChange={(e) => setForm({ ...form, part_name: e.target.value })}
                placeholder="e.g. Water Inlet Valve"
                data-testid="truck-form-name"
              />
            </div>
            <div>
              <Label>Part Number</Label>
              <Input
                value={form.part_number}
                onChange={(e) => setForm({ ...form, part_number: e.target.value })}
                placeholder="e.g. WPW10648041"
                data-testid="truck-form-number"
              />
            </div>
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                min="0"
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                data-testid="truck-form-qty"
              />
            </div>
            <div>
              <Label>Low-Stock Threshold</Label>
              <Input
                type="number"
                min="0"
                value={form.min_threshold}
                onChange={(e) => setForm({ ...form, min_threshold: e.target.value })}
                data-testid="truck-form-threshold"
              />
            </div>
            {isPrivileged && (
              <div className="sm:col-span-2">
                <Label>Assign to Tech</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  value={form.tech_id}
                  onChange={(e) => setForm({ ...form, tech_id: e.target.value })}
                  data-testid="truck-form-tech"
                >
                  <option value="">Myself</option>
                  {techs.map((t) => (
                    <option key={t.id} value={t.id}>{t.full_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="sm:col-span-2">
              <Label>Notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Fits Whirlpool washers"
                data-testid="truck-form-notes"
              />
            </div>

            {/* Photo picker */}
            <div className="sm:col-span-2">
              <Label>Photos (optional)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {pendingPhotos.map((f, i) => {
                  const url = URL.createObjectURL(f);
                  return (
                    <div key={i} className="relative">
                      <img
                        src={url}
                        alt=""
                        className="w-16 h-16 object-cover rounded-lg border"
                        onLoad={() => URL.revokeObjectURL(url)}
                      />
                      <button
                        type="button"
                        onClick={() => removePendingPhoto(i)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                <label
                  htmlFor="truck-cam-input"
                  className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 flex items-center justify-center hover:border-violet-400 hover:text-violet-600 cursor-pointer"
                  data-testid="truck-form-photo-camera"
                >
                  <Camera className="w-5 h-5" />
                </label>
                <label
                  htmlFor="truck-gal-input"
                  className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 flex items-center justify-center hover:border-violet-400 hover:text-violet-600 cursor-pointer"
                  data-testid="truck-form-photo-gallery"
                >
                  <ImageIcon className="w-5 h-5" />
                </label>
                <input
                  id="truck-cam-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  className="sr-only"
                  onChange={(e) => {
                    onPickPhotos(e.target.files);
                    e.target.value = "";
                  }}
                />
                <input
                  id="truck-gal-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="sr-only"
                  onChange={(e) => {
                    onPickPhotos(e.target.files);
                    e.target.value = "";
                  }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Camera icon opens the camera; picture icon picks from gallery.</p>
            </div>

            <div className="sm:col-span-2 flex justify-end gap-2 pb-4">
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button
                onClick={submitAdd}
                disabled={saving}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="truck-form-submit"
              >
                {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Items list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-500 text-sm">
            {isSearching
              ? `No parts found matching "${debouncedSearch}"`
              : "No parts in this truck yet. Tap “Add Part” to get started."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((item) => {
            const isLow = (item.quantity || 0) <= (item.min_threshold || 1);
            const isEditing = editingId === item.id;
            return (
              <Card key={item.id} className={isLow ? "border-amber-400" : ""}>
                <CardContent className="p-3">
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={editDraft.part_name}
                        onChange={(e) => setEditDraft({ ...editDraft, part_name: e.target.value })}
                        placeholder="Part name"
                      />
                      <Input
                        value={editDraft.part_number}
                        onChange={(e) => setEditDraft({ ...editDraft, part_number: e.target.value })}
                        placeholder="Part number"
                      />
                      <div>
                        <Label className="text-xs">Qty</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editDraft.quantity}
                          onChange={(e) => setEditDraft({ ...editDraft, quantity: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Threshold</Label>
                        <Input
                          type="number"
                          min="0"
                          value={editDraft.min_threshold}
                          onChange={(e) => setEditDraft({ ...editDraft, min_threshold: e.target.value })}
                        />
                      </div>
                      <Input
                        className="col-span-2"
                        value={editDraft.notes}
                        onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                        placeholder="Notes"
                      />
                      <div className="col-span-2 flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                        <Button size="sm" onClick={saveEdit} className="bg-violet-600 hover:bg-violet-700">
                          <Save className="w-4 h-4 mr-1" /> Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      {/* Thumbnail (or placeholder) */}
                      <button
                        type="button"
                        onClick={() => {
                          if ((item.images || []).length > 0) {
                            setLightbox({ open: true, item, index: 0 });
                          } else if (canEditRow(item)) {
                            rowUploadTargetRef.current = item.id;
                            rowUploadRef.current?.click();
                          }
                        }}
                        className="relative shrink-0 w-14 h-14 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center"
                        data-testid={`truck-thumb-${item.id}`}
                        aria-label={
                          (item.images || []).length > 0 ? "View photos" : "Add photo"
                        }
                      >
                        {(item.images || []).length > 0 ? (
                          <>
                            <img
                              src={buildImgUrl(item.images[0])}
                              alt={item.part_name}
                              className="w-full h-full object-cover"
                            />
                            {item.images.length > 1 && (
                              <span className="absolute bottom-0 right-0 bg-slate-900/70 text-white text-[10px] px-1.5 py-0.5 rounded-tl-md">
                                +{item.images.length - 1}
                              </span>
                            )}
                          </>
                        ) : canEditRow(item) ? (
                          uploadingRowId === item.id ? (
                            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                          ) : (
                            <Camera className="w-5 h-5 text-slate-400" />
                          )
                        ) : (
                          <ImageIcon className="w-5 h-5 text-slate-300" />
                        )}
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 truncate">{item.part_name}</p>
                          {isLow && (
                            <span
                              className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                              data-testid="truck-low-badge"
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {(item.quantity || 0) === 0 ? "Out" : "Low"}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 font-mono">{item.part_number}</p>
                        {showTechColumn && (
                          <p className="text-xs text-slate-500 mt-0.5">Tech: {item.tech_name}</p>
                        )}
                        {item.notes && <p className="text-xs text-slate-400 mt-0.5">{item.notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {canEditRow(item) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => adjustQty(item, -1)}
                              disabled={(item.quantity || 0) <= 0}
                              className="h-8 w-8 p-0"
                              data-testid={`truck-qty-dec-${item.id}`}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <span
                          className={`min-w-[2rem] text-center font-semibold ${
                            isLow ? "text-amber-700" : "text-slate-900"
                          }`}
                          data-testid={`truck-qty-${item.id}`}
                        >
                          {item.quantity}
                        </span>
                        {canEditRow(item) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => adjustQty(item, +1)}
                              className="h-8 w-8 p-0"
                              data-testid={`truck-qty-inc-${item.id}`}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                rowUploadTargetRef.current = item.id;
                                rowUploadRef.current?.click();
                              }}
                              className="h-8 px-2 text-xs text-violet-700 hover:text-violet-900"
                              data-testid={`truck-add-photo-${item.id}`}
                              title="Add photo"
                            >
                              <Camera className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRestockDialog(item)}
                              className="h-8 px-2 text-xs text-emerald-700 hover:text-emerald-900"
                              data-testid={`truck-reorder-${item.id}`}
                              title="Request restock from admin"
                            >
                              <PackagePlus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openQuickOrder(item)}
                              disabled={(item.quantity || 0) <= 0}
                              className="h-8 px-2 text-xs text-emerald-700 hover:text-emerald-900"
                              data-testid={`truck-use-job-${item.id}`}
                              title="Use for job (creates order)"
                            >
                              <ShoppingCart className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openTransfer(item)}
                              disabled={(item.quantity || 0) <= 0}
                              className="h-8 px-2 text-xs text-blue-700 hover:text-blue-900"
                              data-testid={`truck-transfer-${item.id}`}
                              title="Transfer to another tech"
                            >
                              <ArrowRightLeft className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEdit(item)}
                              className="h-8 px-2 text-xs"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(item)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Hidden file input for row-level photo add (camera-first) */}
      <input
        ref={rowUploadRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          const targetId = rowUploadTargetRef.current;
          if (!file || !targetId) return;
          const item = items.find((it) => it.id === targetId);
          if (item) await addPhotoToRow(item, file);
        }}
      />

      {/* Lightbox */}
      {lightbox.open && lightbox.item && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox({ open: false, item: null, index: 0 })}
          data-testid="truck-lightbox"
        >
          <div
            className="relative max-w-3xl w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLightbox({ open: false, item: null, index: 0 })}
              className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {(() => {
              const imgs = lightbox.item.images || [];
              const idx = Math.min(lightbox.index, Math.max(imgs.length - 1, 0));
              const currentPath = imgs[idx];
              return (
                <>
                  {currentPath ? (
                    <img
                      src={buildImgUrl(currentPath)}
                      alt={lightbox.item.part_name}
                      className="max-h-[70vh] w-auto rounded-lg object-contain bg-slate-900"
                    />
                  ) : (
                    <div className="text-white/70 py-16">No photos yet.</div>
                  )}

                  {/* Nav dots */}
                  {imgs.length > 1 && (
                    <div className="flex gap-2 mt-3">
                      {imgs.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox((lb) => ({ ...lb, index: i }))}
                          className={`w-2.5 h-2.5 rounded-full ${
                            i === idx ? "bg-white" : "bg-white/40"
                          }`}
                          aria-label={`Photo ${i + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  <div className="mt-3 text-white text-center">
                    <p className="font-medium">{lightbox.item.part_name}</p>
                    <p className="text-xs text-white/60 font-mono">{lightbox.item.part_number}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    {canEditRow(lightbox.item) && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            rowUploadTargetRef.current = lightbox.item.id;
                            rowUploadRef.current?.click();
                          }}
                        >
                          <Camera className="w-4 h-4 mr-1" /> Add
                        </Button>
                        {currentPath && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteRowImage(lightbox.item, currentPath)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
      {/* Transfer to Tech dialog */}
      {transferItem && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setTransferItem(null)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="truck-transfer-dialog"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Transfer to Tech</h3>
              <button onClick={() => setTransferItem(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              <span className="font-mono">{transferItem.part_number}</span> · {transferItem.part_name}<br />
              You have <b>{transferItem.quantity}</b> in truck
            </p>
            <div className="space-y-3">
              <div>
                <Label>Recipient tech</Label>
                <select
                  value={transferTechId}
                  onChange={(e) => setTransferTechId(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="transfer-tech-select"
                >
                  <option value="">Pick a tech...</option>
                  {techs
                    .filter((t) => t.id !== transferItem.tech_id)
                    .map((t) => (
                      <option key={t.id} value={t.id}>{t.full_name}</option>
                    ))}
                </select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  max={transferItem.quantity}
                  value={transferQty}
                  onChange={(e) => setTransferQty(e.target.value)}
                  data-testid="transfer-qty"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Held in escrow until the recipient accepts (returned if declined).
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setTransferItem(null)}>Cancel</Button>
                <Button
                  onClick={submitTransfer}
                  disabled={transferBusy}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="transfer-submit"
                >
                  {transferBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Send Request
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restock Request dialog */}
      {restockOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRestockOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="restock-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Request Restock</h3>
              <button onClick={() => setRestockOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Sends a request to admin. Once ordered, the part is auto-added to your truck.</p>
            <div className="space-y-3">
              <div>
                <Label>Part Name *</Label>
                <Input value={restockForm.part_name} onChange={(e) => setRestockForm({ ...restockForm, part_name: e.target.value })} placeholder="e.g. Water Inlet Valve" data-testid="restock-part-name" />
              </div>
              <div>
                <Label>Part Number *</Label>
                <Input value={restockForm.part_number} onChange={(e) => setRestockForm({ ...restockForm, part_number: e.target.value })} placeholder="e.g. WPW10648041" data-testid="restock-part-number" />
              </div>
              <div>
                <Label>Brand *</Label>
                <select
                  value={restockForm.brand}
                  onChange={(e) => setRestockForm({ ...restockForm, brand: e.target.value })}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="restock-brand"
                >
                  <option value="">Pick brand...</option>
                  {brands.map((b) => (<option key={b} value={b}>{b}</option>))}
                </select>
                {restockForm.brand === "Other" && (
                  <Input value={restockCustomBrand} onChange={(e) => setRestockCustomBrand(e.target.value)} placeholder="Custom brand" className="mt-1" />
                )}
              </div>
              <div>
                <Label>Quantity *</Label>
                <Input type="number" min="1" value={restockForm.quantity} onChange={(e) => setRestockForm({ ...restockForm, quantity: e.target.value })} data-testid="restock-qty" />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input value={restockForm.notes} onChange={(e) => setRestockForm({ ...restockForm, notes: e.target.value })} placeholder="Anything admin should know" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setRestockOpen(false)}>Cancel</Button>
                <Button onClick={submitRestock} disabled={restockBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="restock-submit">
                  {restockBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Send to Admin
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Approve Restock dialog */}
      {approveOrder && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setApproveOrder(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="restock-approve-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Approve & Order</h3>
              <button onClick={() => setApproveOrder(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              <span className="font-mono">{approveOrder.part_number}</span> · {approveOrder.part_name}<br />
              For <b>{approveOrder.requester_tech_name}</b> · Qty <b>{approveOrder.quantity}</b> · 🏷️ {approveOrder.brand}
            </p>
            <div className="space-y-3">
              <div>
                <Label>Source</Label>
                <select value={approveForm.part_source} onChange={(e) => { setApproveForm({ ...approveForm, part_source: e.target.value }); setApprovePreview(null); }} className="w-full border rounded-md px-3 py-2 text-sm bg-white" data-testid="restock-approve-source">
                  <option value="Marcone">Marcone</option>
                  <option value="Amazon">Amazon</option>
                  <option value="Outsources">Outsources</option>
                </select>
              </div>
              <div>
                <Label>Part Cost ($) *</Label>
                <Input type="number" step="0.01" min="0" value={approveForm.part_cost} onChange={(e) => { setApproveForm({ ...approveForm, part_cost: e.target.value }); setApprovePreview(null); }} data-testid="restock-approve-cost" />
              </div>
              <Button type="button" variant="outline" onClick={calcApprovePreview} className="w-full" data-testid="restock-approve-calc">
                Calculate Markup
              </Button>
              {approvePreview && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
                  <p>Customer price: <b className="text-emerald-700">${(approvePreview.total_price ?? 0).toFixed(2)}</b></p>
                  <p className="text-xs text-slate-500">${(approvePreview.total_per_unit ?? 0).toFixed(2)} × {approveOrder.quantity} · Markup {approvePreview.markup_percentage ?? 0}%</p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setApproveOrder(null)}>Cancel</Button>
                <Button onClick={submitApprove} disabled={approveBusy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="restock-approve-submit">
                  {approveBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Approve & Create Order
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Restock dialog */}
      {declineTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDeclineTarget(null)}>
          <div className="bg-white rounded-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="restock-decline-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Decline Restock</h3>
              <button onClick={() => setDeclineTarget(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Declining <span className="font-mono">{declineTarget.part_number}</span> for {declineTarget.requester_tech_name}
            </p>
            <div className="space-y-3">
              <div>
                <Label>Reason (optional)</Label>
                <Input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="e.g. Out of stock at supplier" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDeclineTarget(null)}>Cancel</Button>
                <Button onClick={submitDecline} className="bg-red-600 hover:bg-red-700" data-testid="restock-decline-submit">Confirm Decline</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Order (Use for Job) dialog */}
      {quickOrderItem && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setQuickOrderItem(null)}
        >
          <div
            className="bg-white rounded-xl w-full max-w-sm p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="truck-quickorder-dialog"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900">Use for Job</h3>
              <button onClick={() => setQuickOrderItem(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              <span className="font-mono">{quickOrderItem.part_number}</span> · {quickOrderItem.part_name}<br />
              Available: <b>{quickOrderItem.quantity}</b>
            </p>
            <div className="space-y-3">
              <div>
                <Label>Job Number</Label>
                <Input
                  value={quickOrderForm.job_number}
                  onChange={(e) => setQuickOrderForm({ ...quickOrderForm, job_number: e.target.value })}
                  placeholder="e.g. 12345"
                  data-testid="quickorder-job"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Qty</Label>
                  <Input
                    type="number"
                    min="1"
                    max={quickOrderItem.quantity}
                    value={quickOrderForm.quantity}
                    onChange={(e) => setQuickOrderForm({ ...quickOrderForm, quantity: e.target.value })}
                    data-testid="quickorder-qty"
                  />
                </div>
                <div>
                  <Label>Part Cost ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={quickOrderForm.part_cost}
                    onChange={(e) => {
                      setQuickOrderForm({ ...quickOrderForm, part_cost: e.target.value });
                      setQuickOrderPreview(null);
                    }}
                    data-testid="quickorder-cost"
                  />
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={calcQuickOrderPreview}
                className="w-full"
                data-testid="quickorder-calc"
              >
                Calculate Markup
              </Button>
              {quickOrderPreview && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
                  <p>
                    Customer price: <b className="text-emerald-700">${(quickOrderPreview.total_price ?? 0).toFixed(2)}</b>
                  </p>
                  <p className="text-xs text-slate-500">
                    ${(quickOrderPreview.total_per_unit ?? 0).toFixed(2)} × {parseInt(quickOrderForm.quantity, 10) || 1}
                    {" · "}Markup {quickOrderPreview.markup_percentage ?? 0}%
                    {quickOrderPreview.tax_amount ? ` · Tax $${quickOrderPreview.tax_amount.toFixed(2)}` : ""}
                    {quickOrderPreview.minimum_applied ? " · min applied" : ""}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setQuickOrderItem(null)}>Cancel</Button>
                <Button
                  onClick={submitQuickOrder}
                  disabled={quickOrderBusy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="quickorder-submit"
                >
                  {quickOrderBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Create Order
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
